/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import React, { useEffect, useMemo, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import moment from 'moment';

import RoundedInput from '../../components/Inputs/RoundedInput';
import EmojiPickerWithButton from '../../components/EmojiPickerWithButton';
import Textarea from '../../components/Textarea';
import Dropdown from '../../components/Dropdown';
import DatePicker from '../../components/DatePicker';
import ConfirmButton from '../../components/Buttons/ConfirmButton';
import FilledButton from '../../components/Buttons/FilledButton';
import InfoIcon from '../../components/InfoIcon';
import EmailReminderService from '../../services/EmailReminderService';
import validateEmail from '../../helpers/validateEmail';
import closeIcon from '../../assets/images/closeIcon.svg';
import classes from '../SetReminderModal/styles.module.scss';

export default function NotificationManagerModal({
  show,
  handleClose,
  meetingId,
  reminder,
  refetch,
}) {
  const [emails, setEmails] = useState('');
  const [message, setMessage] = useState('');
  const [emailsValidationError, setEmailsValidationError] = useState('');
  const [dropdownValue, setDropdownValue] = useState(6);
  const [sendAt, setSendAt] = useState('');
  const [fetchedReminder, setFetchedReminder] = useState(null);
  const [areEmailsValidated, setAreEmailsValidated] = useState(false);

  const existingReminder = useMemo(() => {
    return fetchedReminder || reminder || null;
  }, [fetchedReminder, reminder]);

  const isEdit = !!existingReminder;

  const handleSubmit = async () => {
    let isValid = true;
    setEmailsValidationError(null);

    let emailsList = [];

    if (!emails) {
      isValid = true;
    } else {
      emailsList = emails.split(';').join(',').split(',');
      emailsList = emailsList.map((mail) => mail.trim());

      const isEveryEmailValid = emailsList.every((mail) => validateEmail(mail));

      if (!isEveryEmailValid) {
        setEmailsValidationError('Emails must be valid');
        isValid = false;
      }
    }

    if (isValid) {
      const date = moment(sendAt).startOf('day').toISOString();
      const reminderData = {
        emails: emailsList,
        subject: 'Reminder',
        text: message,
        sendAt: date,
        type: 'meetingUpdate',
      };

      try {
        if (isEdit && existingReminder) {
          await EmailReminderService.updateReminder({
            reminderId: existingReminder.id,
            ...reminderData,
          });
        } else if (meetingId) {
          await EmailReminderService.createEmailReminder({
            meetingId,
            ...reminderData,
          });
        }

        refetch?.();
        handleClose();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error);
      }
    }
  };

  // Fetch latest reminder when modal opens
  useEffect(() => {
    if (!show) {
      setFetchedReminder(null);
      return;
    }
    const loadLatest = async () => {
      if (!meetingId) return;
      try {
        const latest = await EmailReminderService.getReminder({ meetingId });
        setFetchedReminder(latest);
      } catch (error) {
        // Ignore if not found; keep existing data
      }
    };
    loadLatest();
  }, [show, meetingId]);

  // Populate form when modal opens or reminder changes
  useEffect(() => {
    if (!show) {
      setEmailsValidationError(null);
      setMessage('');
      setEmails('');
      setDropdownValue(6);
      setSendAt('');
      return;
    }

    if (existingReminder) {
      setEmails(existingReminder.emails?.join('; ') || '');
      setMessage(existingReminder.text || '');
      setSendAt(moment(existingReminder.sendAt).toDate());
      setDropdownValue('');
    } else if (dropdownValue) {
      const date = moment()
        .add(dropdownValue, 'months')
        .startOf('day')
        .toDate();
      setSendAt(date);
    }
  }, [show, existingReminder, dropdownValue]);

  return (
    <Modal
      show={show}
      centered
      contentClassName={classes.SetReminderModal}
      dialogClassName={classes.dialog}
      backdropClassName={classes.backdrop}
    >
      <i className={classes.closeIcon} onClick={handleClose}>
        <img src={closeIcon} alt="Close" />
      </i>
      <header>
        <h1>Set Reminder</h1>
        <p className={classes.subtitle}>
          Set a reminder to review this video and ensure it&apos;s still up-to-date
        </p>
        <p className={classes.subtitle}>
          No need to re-save or publish.
        </p>
      </header>

      <div className={classes.form}>
        <div className={classes.row}>
          <div className={classes.col}>
            <div className={classes.title}>
              <h2>Remind me in</h2>
            </div>
            <Dropdown
              options={[
                { label: '3 months', value: 3 },
                { label: '6 months', value: 6 },
                { label: '9 months', value: 9 },
                { label: '12 months', value: 12 },
              ]}
              placeholder="Select"
              currentOption={dropdownValue}
              setCurrentOption={setDropdownValue}
            />
          </div>
        </div>

        <div className={classes.row}>
          <div className={classes.divider}>
            <div />
            or <div />
          </div>
        </div>

        <div className={classes.row}>
          <div className={classes.col}>
            <div className={classes.title}>
              <h2>Set Custom Date</h2>
            </div>
            <DatePicker date={sendAt} setDate={setSendAt} />
          </div>
        </div>

        <div className={`${classes.row} ${classes.inviteRow}`}>
          <div
            className={`${classes.inviteUsersContainer} ${classes.col}`}
          >
            <div className={classes.title}>
              <h2>
                Remind Others
                <div className={classes.infoIconContainer}>
                  <InfoIcon
                    text={`Use a semicolon ";" after each email address when adding
                    multiple emails`}
                  />
                </div>
              </h2>
            </div>
            <RoundedInput
              style={{ height: 64 }}
              type="text"
              placeholder="Add Email Addresses"
              value={emails}
              onChange={(event) => setEmails(event.target.value)}
              setValue={setEmails}
              users
              requireSuggestionSelection
              onValidationChange={setAreEmailsValidated}
            />
            {emailsValidationError && (
              <div className={classes.error}>{emailsValidationError}</div>
            )}
          </div>
        </div>

        <div className={classes.row}>
          <div className={classes.col}>
            <div className={classes.title}>
              <h2>Add Note</h2>
            </div>
            <div className={classes.messageContainer}>
              <Textarea
                placeholder="Start typing..."
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                style={{ height: 172 }}
              />
              <EmojiPickerWithButton
                setCommentText={setMessage}
                buttonBottom="17px"
                buttonRight="20px"
                pickerTop="52px"
              />
            </div>
          </div>
        </div>

      </div>

      <footer>
        <ConfirmButton
          onClick={handleSubmit}
          isDisabled={!areEmailsValidated && emails.trim()}
        />
        <FilledButton
          type="button"
          className={classes.skipButton}
          onClick={handleClose}
          width={133}
          color="black"
        >
          Close
        </FilledButton>
      </footer>
    </Modal>
  );
}

